import { useAdminSearchExamCenterProctorsQuery, FilterOperator } from '@exam-center/graphql';
import { useNavigate } from '@exam-center/router';
import {
  type GridEventListener,
  type GridRowParams,
  GroupOutlinedIcon,
  NoRowsOverlay,
} from '@shared/components';

import { BaseAdminProctorList } from '../../components';
import { ORDER_TYPE, PROCTOR_TYPE_VARIANT_MAP } from '../../constants';
import { useAdminProctorListSearchParams } from '../../hooks';

import { type AdminProctorListItem, columns } from './column';

export default function AdminProctorList() {
  const [
    { page, pageSize, sortOrder, search, proctorType, examCenter },
    { changePageNumber, changePageSize, changeSorting },
  ] = useAdminProctorListSearchParams();
  const navigate = useNavigate();
  const isFiltered = search.length > 0 || proctorType.length > 0 || examCenter.length > 0;

  const { data, loading } = useAdminSearchExamCenterProctorsQuery({
    variables: {
      searchExamCenterProctorsInput: {
        paging: {
          currentPage: page,
          pageSize: pageSize,
        },
        order: {
          field: 'createdAt',
          type: ORDER_TYPE[sortOrder],
        },
        filter: {
          searchText: {
            fields: ['fullName'],
            value: search,
          },
          conditions: [
            {
              field: 'type',
              value: proctorType.length > 0 ? proctorType : null,
              operator: FilterOperator.In,
            },
            {
              field: 'examCenterCode',
              value: examCenter.length > 0 ? examCenter : null,
              operator: FilterOperator.In,
            },
          ].filter((condition) => condition.value !== null),
        },
      },
    },
  });

  const examCenterProctors = data?.ecAdminSearchExamCenterProctors.examCenterProctors ?? [];
  const totalExamCenterProctors = data?.ecAdminSearchExamCenterProctors.total ?? 0;
  const examCenterProctorItems = examCenterProctors.map((item) => ({
    id: item.id,
    fullName: [item.titleTh, item.firstNameTh, item.middleNameTh, item.lastNameTh]
      .filter(Boolean)
      .join(' '),
    type: PROCTOR_TYPE_VARIANT_MAP[item.type],
    examCenter: item.examCenterCode,
    createdAt: item.createdAt,
  }));

  const handleRowClick: GridEventListener<'rowClick'> = (
    params: GridRowParams<AdminProctorListItem>,
  ) => {
    navigate(`/admin/exam-proctors/proctors/:examCenterProctorId`, {
      params: {
        examCenterProctorId: params.row.id,
      },
    });
  };

  return (
    <BaseAdminProctorList
      columns={columns}
      rows={examCenterProctorItems}
      slots={{
        noRowsOverlay: () =>
          isFiltered ? (
            <NoRowsOverlay />
          ) : (
            <NoRowsOverlay title='ยังไม่มีรายชื่อผู้คุมสอบ' icon={GroupOutlinedIcon} />
          ),
      }}
      loading={loading}
      onRowClick={handleRowClick}
      slotProps={{
        footer: {
          total: totalExamCenterProctors,
          pagination: {
            page: page,
            count: totalExamCenterProctors,
            rowsPerPage: pageSize,
            onPageChange: (_, page) => {
              changePageNumber(page);
            },
            onRowsPerPageChange: (event) => {
              changePageSize(parseInt(event.target.value));
            },
          },
        },
      }}
      onSortModelChange={(sorting) => {
        const sortModel = sorting[0];
        if (sortModel?.field && sortModel?.sort) {
          return changeSorting({
            sortOrder: sortModel.sort,
            sortBy: sortModel.field,
          });
        }

        return changeSorting({
          sortOrder: null,
          sortBy: null,
        });
      }}
    />
  );
}
